import { useState, type ReactNode } from 'react';

export interface StaffAvatarProps {
  /** The screen's own avatar class: it keeps size, colour and the rounded-square shape. */
  readonly className: string;
  /** The Backend avatar presentation URL, if Identity holds one. */
  readonly imageUrl?: string | null | undefined;
  /** What stands in without a usable picture: the initial or initials. */
  readonly fallback: ReactNode;
  /** Decorations drawn over the avatar, such as the HR presence dot. */
  readonly children?: ReactNode;
  /** Hidden from assistive technology unless it carries its own labelled decoration. */
  readonly decorative?: boolean;
}

/**
 * A staff member's picture: the uploaded photo or the kitchen-crew avatar the
 * Backend gives every new employee. A missing URL or a picture that fails to
 * load shows the initials instead, never a broken image.
 */
export function StaffAvatar({ className, imageUrl, fallback, children, decorative = true }: StaffAvatarProps) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const picture = typeof imageUrl === 'string' && imageUrl.trim() !== '' && imageUrl !== failedUrl ? imageUrl : null;
  return (
    <span aria-hidden={decorative ? 'true' : undefined} className={`${className} staff-avatar`} data-avatar={picture === null ? 'initials' : 'image'}>
      {picture === null ? fallback : <img alt="" className="staff-avatar__image" decoding="async" draggable={false} onError={() => setFailedUrl(picture)} src={picture} />}
      {children}
    </span>
  );
}
